import { Component, OnInit, Output, EventEmitter, Input, Inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, of, Subject } from 'rxjs';
import { LeadSourceOption } from 'app/models/dto/lead-source-option.model';
import { LeadSourceOptionService } from 'app/services/lead-source-option/lead-source-option.service';
import { NewLeadDto } from 'app/models/dto/add-new-lead';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { Store } from '@ngxs/store';
import { IndustriesService, Industry } from 'app/services/industries/industries.service';
import { Router } from '@angular/router';
import { CreateNewBorrowerAlert } from 'app/store/borrower/alerts/borrower-alerts.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';

interface BorrowerPostResponseData {
  borrower: {
    id: number;
    userId: number;
  };
}

@Component({
  selector: 'app-add-new-lead-form',
  templateUrl: './add-new-lead-form.component.html',
  styleUrls: [
    './add-new-lead-form.component.scss',
    '../../scss/material/_dialog.scss',
  ],
  standalone: false
})

export class AddNewLeadFormComponent implements OnInit {

  @Output() newBusinessAlertDismissed = new EventEmitter<void>();
  @Output() callWrapUpSubmitted = new EventEmitter<void>();
  @Input() callWrapUp = false;

  loading$: Observable<boolean>;
  leadSourceOptions$: Observable<LeadSourceOption[]>;
  showErrorMessage$ = new Subject();

  currentUser: LenderUser = this.store.selectSnapshot(LenderUserState.lenderUser);
  addBusinessForm = new UntypedFormGroup({});
  formOptions: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];
  model: Partial<NewLeadDto> = {
    email: null,
    leadSource: null,
    first: null,
    last: null,
    title: null,
    mobile_phone: null,
    timeInBusiness: null,
    average_monthly_sales: null,
    creditScore: null,
    industryName: null,
    business_name: null,
    userId: null
  };
  isFranchise: boolean = false;
  leadSourceOptions: LeadSourceOption[] = [];
  errMessage: string;
  navigateToNewBorrower: boolean = true;
  industries: Industry[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private router: Router,
    private store: Store,
    private industriesService: IndustriesService,
    private leadSourceOptionService: LeadSourceOptionService,
    private dialogRef: MatDialogRef<AddNewLeadFormComponent>,
    private eventBusService: EventBusService,
    private localeSettingService: LocaleSettingService
  ) {}

  ngOnInit(): void {
    if (this.currentUser && this.currentUser.team.type === 'franchise') {
      this.isFranchise = true;
    }
    this.industries = this.industriesService.getIndustries();
    this.navigateToNewBorrower = this.data?.navigateToNewBorrower || this.navigateToNewBorrower;
    this.leadSourceOptions$ = this.leadSourceOptionService.get();
    this.leadSourceOptions$.subscribe((options) => this.leadSourceOptions = options);
    this.fields = [
      {
        key: 'leadSource',
        wrappers: ['form-field'],
        type: 'autocomplete',
        templateOptions: {
          label: 'Lead Source',
          placeholder: 'search for a name or phone number',
          required: true,
          getOptions: (term) => this.filterLeadSourceOptions(term),
          attributes: {
            'data-cy': 'leadSource',
          },
        },
      },
      {
        key: 'adgroup',
        type: 'input',
        templateOptions: {
          label: 'Lead Source Details',
          required: false,
        },
        hideExpression: () => !this.isFranchise,
      },
      {
        key: 'business_name',
        type: 'input',
        templateOptions: {
          label: 'Business Name',
          required: true,
          attributes: {
            'data-cy': 'businessName',
          },
        },
      },
      {
        key: 'first',
        type: 'input',
        templateOptions: {
          label: 'Contact First Name',
          required: true,
          attributes: {
            'data-cy': 'contactFirstName',
          },
        },
      },
      {
        key: 'last',
        type: 'input',
        templateOptions: {
          label: 'Contact Last Name',
          required: true,
          attributes: {
            'data-cy': 'contactLastName',
          },
        },
      },
      {
        key: 'title',
        type: 'input',
        templateOptions: {
          label: 'Title/Role',
          attributes: {
            'data-cy': 'contactTitle',
          },
        },
      },
      {
        key: 'email',
        type: 'input',
        templateOptions: {
          type: 'email',
          label: 'Email',
          placeholder: 'Enter email',
          required: true,
          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z0-9]{2,}$/i,
          attributes: {
            'data-cy': 'contactEmail',
          },
        },
        validation: {
          messages: {
            pattern: 'Please enter valid email',
          },
        },
      },
      {
        key: 'mobile_phone',
        type: 'phone',
        templateOptions: {
          label: 'Mobile Phone',
          placeholder: 'Enter mobile phone',
          required: true,
          attributes: {
            'data-cy': 'phone-number-input',
          },
        },
      },
      {
        key: 'timeInBusiness',
        type: 'number',
        templateOptions: {
          label: 'How long have you been in business (Months)?',
          required: true,
          min: 0,
          max: 99999,
          attributes: {
            'data-cy': 'timeInBusiness',
          },
        },
        hideExpression: () => this.isClientReferral(),
      },
      {
        key: 'average_monthly_sales',
        type: 'currency',
        templateOptions: {
          label: 'Average Month Sales',
          required: true,
          attributes: {
            'data-cy': 'averageMonthlySales',
          },
        },
        expressionProperties: {
          'templateOptions.maskOptions': (model) => ({
            mask: 'separator',
            prefix:
              model.average_monthly_sales &&
              this.localeSettingService.getLocaleCurrencySymbol(),
          }),
        },
        hideExpression: () => this.isClientReferral(),
      },
      {
        key: 'creditScore',
        type: 'input',
        templateOptions: {
          label: 'Personal Credit Score',
          minLength: 3,
          maxLength: 3,
          min: 300,
          max: 900,
          required: true,
          pattern: /^[0-9][0-9][0-9]*$/,
          attributes: {
            'data-cy': 'creditScore',
          },
        },
        hideExpression: () => this.isClientReferral(),
        validation: {
          messages: {
            pattern: 'Please enter a value between 300 and 900',
          },
        },
      },
      {
        key: 'industryName',
        type: 'select',
        templateOptions: {
          label: 'Industry',
          required: true,
          appearance: 'outline',
          attributes: {
            'data-cy': 'industryName',
          },
          options: [
            ...this.industries.map((industry) => ({
              label: industry.display,
              value: industry.value,
            })),
          ],
        },
        hideExpression: () => this.isClientReferral(),
      },
      {
        key: 'zipId',
        type: 'input',
        templateOptions: {
          label: 'Business Zip',
          placeholder: 'Zip Code',
          maxLength: 5,
          required: true,
          pattern: /^[0-9]*$/,
        },
        hideExpression: () => !this.isFranchise,
      },
    ];
  }

  filterLeadSourceOptions(term: string) {
    const leadSourceOptions = this.leadSourceOptions.filter((option) => {
      if (this.isFranchise) {
        return option.searchTerm.toLowerCase().indexOf(term.toLowerCase()) !== -1;
      }
      return option.optLabel.toLowerCase().indexOf(term.toLowerCase()) !== -1;
    }).map((option) => {
      const val = this.isFranchise ? option.searchTerm : option.optLabel;
      return { label: val, value: val };
    });

    return of(leadSourceOptions);
  }

  isClientReferral(): boolean {
    return !!this.model.leadSource && this.model.leadSource === 'Client Referral';
  }

  isOutOfFranchiseArea(userId: number) {
    if (this.isFranchise && userId !== this.currentUser.id) {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'You created a lead that is out of area. Please contact your Support Team if this is a special circumstance.',
        dismissDelay: 5000
      }));
      this.dialogRef.close();

      return true;
    }

    return false;
  }

  handleNewLeadError(err: string | { message: string }) {
    if (typeof err === 'object') {
      const errorMessages: string[] = [];
      errorMessages.push(err.message);
      this.errMessage = errorMessages.join(', ');
    } else if (typeof err === 'string') {
      this.errMessage = err;
    }
    this.loading$ = of(false);
    if (this.errMessage === 'Unauthorized') {
      this.errMessage = 'You are not authorized to create a lead for the provided email address.';
    }
    this.errMessage = this.errMessage ?? 'Something went wrong, please check your data and try again.';
    this.showErrorMessage$.next(true);
  }

  handleNewLeadSuccess(res: BorrowerPostResponseData) {
    if (this.isOutOfFranchiseArea(res.borrower.userId)) {
      return;
    }

    this.loading$ = of(false);
    this.eventBusService.publish(new this.eventBusService.types.AddedNewLeadEvent);
    this.store.dispatch(new CreateNewBorrowerAlert({
      level: 'success',
      message: 'New business added!',
      dismissDelay: 5000
    }));

    if (this.callWrapUp) {
      this.callWrapUpSubmitted.emit();
    } else {
      this.dialogRef.close();
    }

    if (this.navigateToNewBorrower) {
      this.router.navigate(['/borrower/', res.borrower.id]);
    }
  }

  async saveNewLead($event: Partial<NewLeadDto>) {
    const leadSourceOption = this.leadSourceOptions.find(
      (option) => this.isFranchise
        ? option.searchTerm.toLowerCase() === this.model.leadSource?.toLowerCase()
        : option.optLabel.toLowerCase() === this.model.leadSource?.toLowerCase()
    );

    const data: Partial<NewLeadDto> = {
      ...$event,
      ...this.model,
      leadSourceOptionId: leadSourceOption?.id,
      average_monthly_sales: Number(this.model.average_monthly_sales?.toString().replace(',', '')),
      importSource: 'pipeline',
      password: null,
    };

    if (this.isClientReferral() || this.isFranchise) {
      data.userId = this.currentUser.id;
    }

    if (leadSourceOption?.inboundCallActivity === 1) {
      data.touchPoint = 'inboundCall';
    } else {
      data.touchPoint = 'pipeline';
    }

    if (this.isFranchise) {
      data.touchPoint = 'pipeline';
      data.medium = leadSourceOption?.medium;
      data.campaign = leadSourceOption?.campaign;
      data.lenderId = this.currentUser.id;
    }

    let comments = '';

    if ($event?.bankruptcy) {
      comments += 'Bankruptcy info: Yes';
    }

    if ($event?.notes?.pastFunds) {
      comments += '\n Applied for funds in the past: ' + $event.notes.pastFunds;
    }

    data.comments = comments;
    this.loading$ = of(true);

    return this.leadSourceOptionService.addNewLead(data).subscribe({
      next: async (res: BorrowerPostResponseData) => {
        try {
          await this.handleNewLeadSuccess(res);
        } catch (error) {
          this.handleNewLeadError(error);
        }
      },
      error: (err) => this.handleNewLeadError(err)
    });
  }
}
